import { useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { FavoriteIcon, LogoutIcon, QuestionIcon } from '../icons';
import {
  accessTokenProjectsSelector,
  filterSelector,
  favoriteProjectSelector,
  userRoleSelector,
} from '@algoan/dashboard/store/selectors';
import { HeaderLogo } from './header-logo';
import { openOnboarding } from '@algoan/dashboard/store/slice';
import { logout, filterCustomers, patchUserAttributes } from '@algoan/dashboard/store/thunks';
import { Onboarding } from '..';
import { DropdownItem, SelectDropdown } from '../atomic-components';
import { useIntl } from 'react-intl';
import { theme, addAlpha } from '@algoan/deep-dive/theme';

interface ItemRendererProps {
  favoriteProject: string | undefined;
  option: DropdownItem;
}

const SelectedItemRenderer: React.FC<ItemRendererProps> = ({ favoriteProject, option }) => {
  const isFavorite = option.key === favoriteProject;
  return (
    <S.SelectedDropdownItem>
      {isFavorite && (
        <S.FavoriteIconContainer>
          <FavoriteIcon selected />
        </S.FavoriteIconContainer>
      )}
      <S.ProjectText isSelected>{option.label}</S.ProjectText>
    </S.SelectedDropdownItem>
  );
};

const ItemRenderer: React.FC<ItemRendererProps> = ({ favoriteProject, option }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const isFavorite = option.key === favoriteProject;
  return (
    <S.DropdownItem>
      <S.FavoriteIconContainer
        data-tooltip-id="multiprojects_icon_tooltip"
        data-tooltip-content={
          !isFavorite
            ? intl.formatMessage({
                id: 'tooltip_multiprojects.message',
                defaultMessage: 'Check the star to display this project directly during your visits',
                description: 'Tooltip message to display on star hover',
              })
            : ''
        }
      >
        <FavoriteIcon
          onClick={(event) => {
            event.stopPropagation();
            const newFavoriteProject = isFavorite ? '' : option.key;
            dispatch(patchUserAttributes({ favoriteProject: newFavoriteProject }));
          }}
          selected={isFavorite}
          dataTestId={`favorite-icon-${option.key}-${isFavorite ? 'active' : 'inactive'}`}
        />
      </S.FavoriteIconContainer>
      <S.ProjectText>{option.label}</S.ProjectText>
    </S.DropdownItem>
  );
};

export const Header: React.FC<{ shouldAddDropdown?: boolean }> = ({ shouldAddDropdown }) => {
  const dispatch = useDispatch();
  const projects = useSelector(accessTokenProjectsSelector);
  const sortedProjectNames = Object.values(projects).sort();
  const hasManyProjects = Object.keys(projects).length > 1;
  const filterData = useSelector(filterSelector);
  const favoriteProject = useSelector(favoriteProjectSelector);
  const selectedProject = useSelector(filterSelector).project;
  const { isInitiator } = useSelector(userRoleSelector);

  const selectDropdownOptions = sortedProjectNames.map((projectName) => {
    return {
      key: Object.keys(projects).find((projectId) => projects[projectId] === projectName) as string,
      label: projectName,
    };
  });

  const handleClick = useCallback(
    (item: DropdownItem): void => {
      dispatch(filterCustomers({ project: { id: item.key, name: item.label } }));
    },
    [dispatch],
  );

  const showDropdown = hasManyProjects && selectedProject && shouldAddDropdown;

  const handleSelectedItemRenderer = useCallback(
    (option: DropdownItem): React.ReactNode => (
      <SelectedItemRenderer favoriteProject={favoriteProject} option={option} />
    ),
    [favoriteProject],
  );

  const handleItemRenderer = useCallback(
    (option: DropdownItem): React.ReactNode => <ItemRenderer favoriteProject={favoriteProject} option={option} />,
    [favoriteProject],
  );

  return (
    <S.Container data-testid="header">
      <HeaderLogo />
      <S.RightSubContainer>
        {showDropdown && (
          <S.DropdownContainer data-testid="multi-projects-dropdown">
            <SelectDropdown
              variant="tertiary"
              onClick={handleClick}
              options={selectDropdownOptions}
              value={{ key: filterData.project?.id as string, label: filterData.project?.name as string }}
              selectedItemRenderer={handleSelectedItemRenderer}
              itemRenderer={handleItemRenderer}
            />
          </S.DropdownContainer>
        )}
        {!isInitiator && (
          <S.QuestionIconContainer
            onClick={() => {
              dispatch(openOnboarding());
            }}
          >
            <QuestionIcon />
          </S.QuestionIconContainer>
        )}
        <S.Logout
          data-testid="logout-button"
          onClick={() => {
            logAmplitudeEvent(AmplitudeEvent.LOGIN_DISCONNECT);
            dispatch(logout());
          }}
        >
          <S.LogoutIcon />
        </S.Logout>
      </S.RightSubContainer>
      <Onboarding />
    </S.Container>
  );
};

const shadowValue = `0px 3px 5px ${addAlpha(theme.algoanBlue, 0.05)}`;

const S = {
  Container: styled.div`
    background: white;
    box-shadow: ${shadowValue};
    box-sizing: border-box;
    display: flex;
    height: 60px;
    justify-content: space-between;
    line-height: 40px;
    padding: 10px;
    width: 100%;
    z-index: 1000;
    position: sticky;
    top: 0;
    a,
    a:visited {
      display: flex;
      text-decoration: none;
      color: ${(props) => props.theme.algoanBlue};
    }

    @media print {
      display: none;
    }
  `,
  Logout: styled.div`
    color: ${(props) => props.theme.algoanBlue};
    background-color: ${(props) => props.theme.secondary.chalk};
    width: 64px;
    height: 60px;
    margin: -10px -10px 0px 32px;
    cursor: pointer;
    display: flex;
    border-left: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)} 1px solid;

    &:hover {
      background-color: ${(props) => props.theme.iceBlue};
      svg {
        path {
          fill: ${(props) => props.theme.secondary.softBlue3};
        }
      }
    }
  `,
  LogoutIcon: styled(LogoutIcon)`
    margin: auto;
  `,
  QuestionIconContainer: styled.div`
    margin: 3px 0px 3px 40px;
    cursor: pointer;
    &:hover {
      svg {
        path {
          fill: ${(props) => props.theme.secondary.softBlue3};
        }
      }
    }
  `,
  RightSubContainer: styled.div`
    display: flex;
  `,
  DropdownContainer: styled.div``,
  DropdownValueContainer: styled.div`
    display: flex;
    flex-direction: row;
    line-height: 17.57px;
  `,
  Value: styled.div`
    padding-right: 30px;
    padding-left: 8px;
  `,
  FavoriteIconContainer: styled.div`
    margin: 3px 16px 0 0;
    vertical-align: top;
  `,
  ProjectText: styled.div<{ isSelected?: boolean }>`
    ${({ isSelected }) => (isSelected ? 'font-weight: bold;' : '')}
  `,
  SelectedDropdownItem: styled.div`
    display: flex;
  `,
  DropdownItem: styled.div`
    display: flex;
  `,
};
